// The forecast run-store as a real external store (EPIC #157, phase P1).
//
// This wraps the existing, well-tested `lib/forecast` reducer in a module-level
// Zustand store so run-lifecycle state lives OUTSIDE React and is reachable from
// anywhere (commands, subscriptions, the realtime channel) without threading
// `dispatchRun`/`runStateRef` through hooks. The reducer, action creators, and
// selectors do not move — this is a thin shell around them (design §2.1).
//
// The `redux` middleware lets the reducer drop in unchanged: the store exposes
// `dispatch(action)` with the same `(state, action) => state` contract as
// `useReducer`. `devtools` gives the action log + time-travel on the run-store,
// the single biggest "follow the state transformations" win.
//
// Booby-trap §7 #1: code reads the store (`getRunState()` / `useRunStore(selector)`, the
// selectors in `store/selectors.ts`), never a render-time mirror of it — a ref assigned
// during render lags every dispatch until the next render, so an async continuation that
// read one could miss a just-stamped id or file under a selection that had already moved.
import { createStore } from "zustand/vanilla";
import { devtools, redux } from "zustand/middleware";
import { useStore } from "zustand";

import { reducer, initialState } from "../lib/forecast/reducer.js";
import type { RunAction, RunState } from "../lib/forecast/reducer.js";
import { traceActions } from "../lib/telemetry/storeTrace.js";

/**
 * The vanilla run-store singleton. `redux(reducer, initialState)` installs a
 * `dispatch` that runs the reducer; `devtools` mirrors the action stream into the
 * Redux DevTools extension. `traceActions` (between the two) records each action's
 * type and ids to the telemetry diagnostics group after the reducer returns
 * (lib/telemetry/storeTrace.js). Exposes the standard vanilla store API
 * (`getState`/`setState`/`subscribe`/`dispatch`).
 */
const reduxStore = redux(reducer, initialState);
export const runStore = createStore(
  devtools(traceActions(reduxStore) as typeof reduxStore, { name: "runStore" }),
);

/**
 * React binding: subscribe a component to a slice of run-state. The selector
 * sees the domain `RunState` (the `redux`-middleware `dispatch` key is elided
 * from the public type — dispatch via `dispatchRun`).
 */
export const useRunStore = <T>(selector: (state: RunState) => T): T => useStore(runStore, selector);

/**
 * Dispatch a run-domain action. Stable identity (bound once to the module-level
 * store), so it can be imported directly and used in dependency arrays without
 * re-threading — this replaces the `useReducer` dispatch destructured in App.jsx.
 */
export const dispatchRun: (action: RunAction) => RunAction = runStore.dispatch;

/**
 * Read the live run-state synchronously — reflects every dispatch immediately, so an async
 * continuation reads fresh state by the id it captured, with no render-time mirror.
 */
export const getRunState: () => RunState = runStore.getState;

/**
 * Reset the store back to `initialState`. Because the store is a module-level
 * singleton, its state would otherwise leak across mounts — StrictMode's
 * mount/unmount/remount in dev, Fast Refresh, and (later) test mounts. Callers
 * invoke this on unmount. Uses `setState(..., false)` (merge, not replace) so the
 * `redux` middleware's injected `dispatch` key on state is preserved.
 */
export function resetRunStore() {
  runStore.setState(initialState, false);
}
